import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { LoadingComponent, MultiCheckDropdown } from "Components";
import { newAuthService } from "Service/Axios";
import {
  useGroupList,
  useProjectList,
  useRoleList,
  useRulesActionTypeList,
  useRulesActionValueSourceList,
  useRulesTenantList,
  useRulesTriggerList,
  useUserList,
} from "Constant/apiHooks";
import {
  GET_QUESTION_LIST_BY_PROJECT_URL,
  GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
  GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
} from "Constant/api";

const actionTypeOptions = {
  setFieldValue: "SET_FIELD_VALUE",
  clearField: "CLEAR_FIELD",
  setRequired: "SET_REQUIRED",
  assignTask: "ASSIGN_TASK",
};

const valueSourceOptions = {
  fixedValue: "Fixed value",
  copyFromAnotherField: "Copy from another field",
};

export const CreateRule = () => {
  const [formData, setFormData] = React.useState({
    ruleName: "",
    tenantId: "",
    priority: "",
    projectId: "",
    triggerType: "",
    actionType: "",
    actionValueSource: "",
    fieldName: "",
    fieldValue: "",
    copyFromField: "",
    isEmpty: false,
    isRequired: false,
    assigneeType: "",
  });

  const [errMsg, setErrMsg] = React.useState({});

  const { rulesTenantList, loading: rulesTenantListLoading } =
    useRulesTenantList();
  const { projectList, loading: projectListLoading } = useProjectList();
  const { rulesTriggerList, loading: rulesTriggerListLoading } =
    useRulesTriggerList();
  const { rulesActionTypeList, loading: rulesActionTypeListLoading } =
    useRulesActionTypeList();
  const {
    rulesActionValueSourceList,
    loading: rulesActionValueSourceListLoading,
  } = useRulesActionValueSourceList();
  const {
    userList,
    loading: userListLoading,
    fetchUserList,
  } = useUserList(false);
  const {
    groupList,
    loading: groupListLoading,
    fetchGroupList,
  } = useGroupList(false);
  const {
    roleList,
    loading: roleListLoading,
    fetchRoleList,
  } = useRoleList(false);

  const {
    ruleName,
    tenantId,
    priority,
    projectId,
    triggerType,
    triggerDetails,
    actionType,
    actionValueSource,
    fieldName,
    fieldValue,
    copyFromField,
    isEmpty,
    isRequired,
    assigneeType,
  } = formData;

  // Translation
  const { t } = useTranslation();

  // Condition defaults
  const defaultIds = {
    mainConditionsId: uuid(),
    questionId: uuid(),
  };
  const defaultQuestionsCondions = {
    id: defaultIds.questionId,
    condition: "AND",
    questionId: "",
    operator: "",
    values: [],
  };
  const defaultMainConditions = {
    id: defaultIds.mainConditionsId,
    condition: "AND",
    questions: [{ ...defaultQuestionsCondions }],
    if: "ALL",
  };

  const [mainConditions, setMainConditions] = useState([
    {
      ...defaultMainConditions,
    },
  ]);
  const [questionList, setQuestionList] = useState([]);
  const [questionsData, setQuestionData] = useState(null);

  const isSetFieldValue = actionType === actionTypeOptions.setFieldValue;
  const isClearField = actionType === actionTypeOptions.clearField;
  const isSetRequired = actionType === actionTypeOptions.setRequired;
  const isAssignTask = actionType === actionTypeOptions.assignTask;

  const isFixedValue = actionValueSource === valueSourceOptions.fixedValue;
  const isCopyFromField =
    actionValueSource === valueSourceOptions.copyFromAnotherField;

  const shouldShowValueSourceSection = isSetFieldValue;
  const shouldShowFieldSection =
    isClearField ||
    isSetRequired ||
    isAssignTask ||
    isFixedValue ||
    isCopyFromField;

  const resetByField = {
    actionType: {
      actionValueSource: "",
      fieldName: "",
      fieldValue: "",
      copyFromField: "",
      isRequired: false,
      assigneeType: "",
    },
    actionValueSource: {
      fieldName: "",
      fieldValue: "",
      copyFromField: "",
    },
  };

  const assigneeOptions = useMemo(() => {
    switch (assigneeType) {
      case "user":
        return userList.map((user) => ({
          label: `${user.firstName} ${user.lastName}`,
          value: user.userId,
        }));
      case "role":
        return roleList.map((role) => ({
          label: role.name,
          value: role.gid,
        }));
      case "group":
        return groupList.map((group) => ({
          label: group.name,
          value: group.gid,
        }));
      default:
        return [];
    }
  }, [assigneeType, userList, roleList, groupList]);

  // Handle basic form change
  const handleChange = (e) => {
    const { type, name, value, checked } = e.target;

    setFormData((prev) => {
      const updatedValue = type === "checkbox" ? checked : value;

      return {
        ...prev,
        [name]: updatedValue,
        ...(resetByField[name] || {}),
      };
    });

    if (name === "projectId") {
      getQuestionList(value);
    }

    if (name === "assigneeType") {
      switch (value) {
        case "role":
          fetchRoleList();
          break;
        case "group":
          fetchGroupList();
          break;
        case "user":
          fetchUserList();
          break;
        default:
          break;
      }
    }
  };

  const handleAddMainConditions = () => {
    setMainConditions([...mainConditions, defaultMainConditions]);
  };

  const handleDeleteMainCondition = (id) => {
    const deletedArr = mainConditions.filter((e) => e.id !== id);
    setMainConditions(deletedArr);
  };

  const handleAddQuestionsConditions = (index) => {
    const newArr = [...mainConditions];
    newArr[index].questions.push(defaultQuestionsCondions);
    setMainConditions(newArr);
  };

  const handleDeleteQuestionsCondition = (mainId, questionId) => {
    const updatedQuestionData = { ...questionsData };
    delete updatedQuestionData[questionId];
    setQuestionData(updatedQuestionData);
    const updatedData = mainConditions.map((item) => {
      if (item.id === mainId) {
        const updatedQuestions = item.questions.filter(
          (question) => question.id !== questionId
        );
        return { ...item, questions: updatedQuestions };
      }
      return item;
    });
    setMainConditions(updatedData);
  };

  const handleChangeMainConditions = (e, index) => {
    const newArr = [...mainConditions];
    newArr[index][e.target.name] = e.target.value;
    setMainConditions(newArr);
  };

  const handleChangeQuestionsConditions = (
    e,
    mainIndex,
    quesIndex,
    value,
    questionId
  ) => {
    const newArr = [...mainConditions];
    newArr[mainIndex].questions[quesIndex][e.target.name] = value;
    if (e.target.name === "questionId") {
      getQuestionData(formData.projectId, value, questionId);
      newArr[mainIndex].questions[quesIndex].operator = "";
    }
    setMainConditions(newArr);
  };

  const handleChangeQuestionsConditionsMultipleValues = (
    values,
    mainIndex,
    quesIndex
  ) => {
    const modifiedValues = values.map((option) => option.value);
    const newArr = [...mainConditions];
    newArr[mainIndex].questions[quesIndex].values = modifiedValues;
    setMainConditions(newArr);
  };

  const questionValuesRender = (
    values,
    index,
    quesCond,
    quesIndex,
    selectValues
  ) => {
    if (values.designType === "select") {
      const options =
        values.dropdownValue &&
        values.dropdownValue.length > 0 &&
        values.dropdownValue.map((option) => ({
          value: option.value,
          label: option.name || "",
        }));
      const selectedValue =
        options && options.filter((obj) => selectValues.includes(obj.value));
      return (
        <MultiCheckDropdown
          options={options}
          value={selectedValue}
          onChange={(values) =>
            handleChangeQuestionsConditionsMultipleValues(
              values,
              index,
              quesIndex
            )
          }
        />
      );
    }
    return (
      <input
        type={values.designType}
        className="form-control form-control-md"
        name="values"
        value={quesCond.values}
        onChange={(e) =>
          handleChangeQuestionsConditions(e, index, quesIndex, [e.target.value])
        }
      />
    );
  };

  const getQuestionList = async (projectId) => {
    const payload = {
      gid: projectId,
    };
    const response = await newAuthService(
      GET_QUESTION_LIST_BY_PROJECT_URL,
      "POST",
      payload
    );
    if (response.status && response.data?.length > 0) {
      const updatedData = response.data.map((question) => ({
        value: question.gid,
        label: question.name,
      }));
      setQuestionList(updatedData);
    } else {
      setQuestionList([]);
    }
  };

  const getQuestionData = async (projectId, questionId, qId) => {
    const payload = {
      questionId,
      projectId,
    };
    if (questionId && projectId) {
      const operatorResponse = await newAuthService(
        GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );

      const valuesResponse = await newAuthService(
        GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );
      if (operatorResponse.status && valuesResponse.status) {
        const newObject = {
          [qId]: {
            values: valuesResponse.data,
            operator: operatorResponse.data,
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      } else {
        const newObject = {
          [qId]: {
            values: null,
            operator: [],
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      }
    }
  };

  return (
    <LoadingComponent
      isLoading={
        rulesTenantListLoading ||
        projectListLoading ||
        rulesTriggerListLoading ||
        rulesActionTypeListLoading ||
        rulesActionValueSourceListLoading
      }
    >
      <main className="main">
        <div className="formbuilder-wrapper">
          <div className="formbuilder-view p-3 rounded-end bg-white">
            <h3>Create Rule</h3>
          </div>

          <div className="formbuilder-form p-3 rounded-end bg-white">
            <div className="new-rule-form">
              <div className="mb-3">
                <h6>Rule Name</h6>
                <input
                  type="text"
                  className="form-control"
                  id="ruleName"
                  name="ruleName"
                  value={formData.ruleName}
                  onChange={handleChange}
                  aria-describedby="ruleName"
                  aria-label="Rule Name"
                  placeholder="Enter rule name"
                />
              </div>
              <div className="mb-3">
                <h6>Scope</h6>
                <div className="row">
                  <div className="col-6">
                    <select
                      className="form-select"
                      aria-label="Select Scope"
                      name="tenantId"
                      value={formData.tenantId}
                      onChange={handleChange}
                    >
                      <option value="">Select Tenant</option>
                      {rulesTenantList.map((tenant, idx) => (
                        <option key={`${tenant}-${idx}`} value={tenant}>
                          {tenant}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <select
                      className="form-select"
                      aria-label="Select Scope"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                    >
                      <option value="">Select Priority</option>
                      {rulesTriggerList.map((priority, idx) => (
                        <option key={`${priority}-${idx}`} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 mt-3">
                    <select
                      className="form-select"
                      aria-label="Select Scope"
                      name="projectId"
                      value={formData.projectId}
                      onChange={handleChange}
                    >
                      <option value="">Select Project</option>
                      {projectList.map((project, idx) => (
                        <option key={`${project}-${idx}`} value={project.gid}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <h6>Trigger</h6>
                <select
                  className="form-select"
                  aria-label="Select Trigger"
                  name="triggerType"
                  value={formData.triggerType}
                  onChange={handleChange}
                >
                  <option value="">Select Trigger</option>

                  {rulesTriggerList.map((trigger, idx) => (
                    <option key={`${trigger}-${idx}`} value={trigger}>
                      {trigger}
                    </option>
                  ))}
                </select>
              </div>

              {/* Conditions (If ... And/Or) */}
              <div className="mb-3 bg-light p-3 rounded">
                <h6>Conditions</h6>
                {mainConditions &&
                  mainConditions.length > 0 &&
                  mainConditions.map((mainCond, index) => (
                    <div className="if-wrapper" key={`${mainCond.id}${index}`}>
                      <div className="step-row mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <label>If</label>
                          {index !== 0 && (
                            <button
                              type="button"
                              className="delete-btn"
                              onClick={() =>
                                handleDeleteMainCondition(mainCond.id)
                              }
                            >
                              <i className="bx bx-x"></i>
                            </button>
                          )}
                        </div>

                        <div className="row align-items-center">
                          {index !== 0 && (
                            <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                              <div className="mb-4">
                                <select
                                  className="form-select form-select-md"
                                  name="condition"
                                  value={mainCond.condition}
                                  onChange={(e) =>
                                    handleChangeMainConditions(e, index)
                                  }
                                >
                                  <option value="AND">And</option>
                                  <option value="OR">Or</option>
                                </select>
                              </div>
                            </div>
                          )}
                          <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                            <div className="mb-4">
                              <select
                                className="form-select form-select-md"
                                name="if"
                                value={mainCond.if}
                                onChange={(e) =>
                                  handleChangeMainConditions(e, index)
                                }
                              >
                                <option value="ALL">All</option>
                                <option value="ANY">Any</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                            <div className="mb-4">
                              <strong>If rule</strong>
                            </div>
                          </div>
                        </div>

                        {mainCond.questions &&
                          mainCond.questions.length > 0 &&
                          mainCond.questions.map((quesCond, quesIndex) => (
                            <div
                              className="add-row-wrapper"
                              key={`${quesCond.id}-${quesIndex}`}
                            >
                              <div className="row">
                                <div className="col-lg-4 col-md-5 col-sm-6 col-12">
                                  <div className="mb-4">
                                    <select
                                      className="form-select form-select-md"
                                      name="questionId"
                                      value={quesCond.questionId}
                                      onChange={(e) =>
                                        handleChangeQuestionsConditions(
                                          e,
                                          index,
                                          quesIndex,
                                          e.target.value,
                                          quesCond.id
                                        )
                                      }
                                    >
                                      <option value="">Select</option>
                                      {questionList &&
                                        questionList.length > 0 &&
                                        questionList.map((qstn, qstnIndex) => (
                                          <option
                                            value={qstn.value}
                                            key={`${qstn.value}-${qstnIndex}`}
                                          >
                                            {qstn.label}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-5 col-sm-6 col-12">
                                  <div className="mb-4">
                                    <select
                                      className="form-select form-select-md"
                                      name="operator"
                                      value={quesCond.operator}
                                      onChange={(e) =>
                                        handleChangeQuestionsConditions(
                                          e,
                                          index,
                                          quesIndex,
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option>Select</option>
                                      {questionsData &&
                                        questionsData[quesCond.id] &&
                                        questionsData[quesCond.id].operator &&
                                        questionsData[quesCond.id].operator
                                          .length > 0 &&
                                        questionsData[quesCond.id].operator.map(
                                          (oprtor, oprtorIndex) => {
                                            return (
                                              <option
                                                value={oprtor.name}
                                                key={`${oprtor.name}-${oprtorIndex}`}
                                              >
                                                {oprtor.name}
                                              </option>
                                            );
                                          }
                                        )}
                                    </select>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-5 col-sm-6 col-12">
                                  <div className="mb-4">
                                    {questionsData &&
                                    questionsData[quesCond.id] &&
                                    questionsData[quesCond.id].values ? (
                                      questionValuesRender(
                                        questionsData[quesCond.id].values,
                                        index,
                                        quesCond,
                                        quesIndex,
                                        quesCond.values
                                      )
                                    ) : (
                                      <input
                                        type="text"
                                        disabled
                                        className="form-control form-control-md"
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-2 col-md-5 col-sm-6 col-12">
                                  <div className="d-flex gap-2 mb-4">
                                    <button
                                      type="button"
                                      className="delete-btn"
                                      onClick={() =>
                                        handleAddQuestionsConditions(index)
                                      }
                                    >
                                      <i className="bx bx-plus"></i>
                                    </button>

                                    <button
                                      type="button"
                                      className="delete-btn"
                                      onClick={() =>
                                        handleDeleteQuestionsCondition(
                                          mainCond.id,
                                          quesCond.id
                                        )
                                      }
                                    >
                                      <i className="bx bx-x"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}

                <div className="col-lg-4 col-md-5 col-sm-6 col-12">
                  <div className="bachcolor-color">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-block d-flex justify-content-between align-items-center"
                      onClick={handleAddMainConditions}
                    >
                      {t ? t("common.label.and") : "And"}/
                      {t ? t("common.label.or") : "Or"}{" "}
                      <i className="bx bx-plus bx-sm"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <h6>Action</h6>
                <div className="row mb-3">
                  <div className="col-12">
                    <select
                      className="form-select"
                      aria-label="Select Action"
                      name="actionType"
                      value={formData.actionType}
                      onChange={handleChange}
                    >
                      <option value="">Select Action</option>

                      {rulesActionTypeList.map((actionType, actionTypeIdx) => (
                        <optgroup
                          key={`${actionType.label}-${actionTypeIdx}`}
                          label={actionType.label}
                        >
                          {actionType.options?.map((opt, optIdx) => (
                            <option
                              key={`${opt.value}-${optIdx}`}
                              value={opt.value}
                            >
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Value Source Section */}
                {shouldShowValueSourceSection && (
                  <div className="row mb-3">
                    <div className="col-6">
                      <select
                        className="form-select"
                        name="actionValueSource"
                        value={formData.actionValueSource}
                        onChange={handleChange}
                      >
                        <option value="">Select Value Source</option>
                        {rulesActionValueSourceList.map(
                          (valueSource, valueSourceIdx) => (
                            <option
                              key={`${valueSource}-${valueSourceIdx}`}
                              value={valueSource}
                            >
                              {valueSource}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-6">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        id="isEmpty"
                        name="isEmpty"
                        checked={formData.isEmpty || false}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="isEmpty">
                        Only set this value if the field is empty
                      </label>
                    </div>
                  </div>
                )}

                {/* Field Section */}
                {shouldShowFieldSection && (
                  <div className="row mb-3">
                    {/* Field selector */}
                    <div className="col-6">
                      <select
                        className="form-select"
                        name="fieldName"
                        value={formData.fieldName}
                        onChange={handleChange}
                      >
                        <option value="">Select Field</option>
                        {questionList.map((field, idx) => (
                          <option
                            key={`${field.value}-${idx}`}
                            value={field.value}
                          >
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Fixed value input */}
                    {isFixedValue && (
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control"
                          name="fieldValue"
                          value={formData.fieldValue || ""}
                          onChange={handleChange}
                          placeholder="Enter Field Value"
                        />
                      </div>
                    )}

                    {/* Copy from another field */}
                    {isCopyFromField && (
                      <div className="col-6">
                        <select
                          className="form-select"
                          name="copyFromField"
                          value={formData.copyFromField}
                          onChange={handleChange}
                        >
                          <option value="">Select Field</option>
                          {questionList.map((field, idx) => (
                            <option
                              key={`${field.value}-${idx}`}
                              value={field.value}
                            >
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Assign task → Assignee Type */}
                    {isAssignTask && (
                      <div className="col-6">
                        <select
                          className="form-select"
                          name="assigneeType"
                          value={formData.assigneeType}
                          onChange={handleChange}
                        >
                          <option value="">Select Assignee Type</option>
                          <option value="user">User</option>
                          <option value="role">Role</option>
                          <option value="group">Group</option>
                        </select>
                      </div>
                    )}

                    {/* Mandatory toggle */}
                    {formData.actionType === actionTypeOptions.setRequired && (
                      <div className="col-6">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          id="isRequired"
                          name="isRequired"
                          checked={formData.isRequired || false}
                          onChange={handleChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="isRequired"
                        >
                          Mandatory / Optional
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {/* Assign task → Assignee */}
                {isAssignTask && assigneeType && (
                  <div className="row">
                    <div className="col-12">
                      <MultiCheckDropdown
                        loading={
                          userListLoading || roleListLoading || groupListLoading
                        }
                        options={assigneeOptions}
                        value={formData.assignee}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            assignee: selected,
                          }))
                        }
                        placeholder="Select Assignee"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </LoadingComponent>
  );
};
